"use client";

import type { ReactNode } from "react";
import Link from "@/components/atlas-link";
import { Fragment, useState } from "react";
import { useRenderedPathname } from "@/lib/navigation";
import { useShowHiddenPages } from "@/hooks/use-show-hidden-pages";
import {
  type LucideIcon,
  Bot,
  Check,
  ChevronRight,
  CreditCard,
  Home,
  LifeBuoy,
  Newspaper,
  ShieldCheck,
  SquarePen,
  Wrench,
} from "lucide-react";
import { findActiveAdminNavItem } from "@/components/admin/admin-nav";
import { devNavItems, findActiveDevNavItem } from "@/components/dev/dev-nav";
import { AccountMenu, type AccountMenuUser } from "@/components/account-menu";
import { BrandLogo } from "@/components/brand-logo";
import { LazyAccountDialog, preloadAccountDialog } from "@/components/portal/account-dialog-lazy";
import { PortalAdminSidebarNav } from "@/components/portal/portal-admin-sidebar-nav";
import { PortalMorphingSidebarNav } from "@/components/portal/portal-morphing-sidebar-nav";
import { portalPageIcons as iconMap } from "@/components/portal/portal-page-icons";
import { PasskeyEnrollmentBanner } from "@/components/portal/passkey-enrollment-banner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
  SidebarRail,
  SidebarSeparator,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  isPortalPageListedForMember,
  type PortalPageCategory,
  type PortalPageKey,
  type PortalPageState,
} from "@/lib/portal-pages";
import type { PortalRole } from "@/lib/portal-auth-shared";
import { brandNames } from "@/lib/brand";
import { SUBSCRIBE_URL } from "@/lib/site-metadata";
import { cn } from "@/lib/utils";


const PORTAL_HOME_HREF = "/dashboard";

const NAV_CATEGORY_ORDER: PortalPageCategory[] = [
  "Trackers",
  "Studies",
  "Models",
];

const ADVANCED_AGENT_HREF = "/advanced-agent";
const BASIC_AGENT_HREF = "/agent";

const ADMIN_FULL_BLEED_PATHS = new Set([
  "/admin/users",
  "/admin/knowledge-base",
  "/admin/capacity-risk-transfer",
]);
const PORTAL_FULL_BLEED_PATHS = new Set([
  "/notes",
  "/data-center-diligence",
  "/capacity-risk-transfer",
  "/models",
]);

type DashboardShellUser = AccountMenuUser;

type PortalDashboardShellProps = {
  /** The authoritative Advanced Agent decision (issue #45); see PortalRenderContext. */
  advancedAgentEnabled?: boolean;
  billingManagementEnabled?: boolean;
  capacityRiskTrackerEnabled?: boolean;
  /** Discover published model summaries without granting access to model data. */
  financialModelPreviewsEnabled?: boolean;
  contentCmsHref?: string;
  currentUser?: DashboardShellUser;
  hasPasskey?: boolean;
  notesFeedEnabled?: boolean;
  /** Anonymous, data-free shell used by the public subscriber preview routes. */
  publicPreview?: boolean;
  /** Show the "Developer" sidebar group (developer-tagged non-admins; admins already have the admin entries). */
  showDeveloperTools?: boolean;
  /** Show developerOnly admin console entries (admins who also carry the "developer" tag). */
  showDeveloperAdminItems?: boolean;
  pages: PortalPageState[];
  accessiblePageKeys: readonly PortalPageKey[];
  children: ReactNode;
};

export function PortalDashboardShell({
  advancedAgentEnabled = false,
  billingManagementEnabled = false,
  capacityRiskTrackerEnabled = false,
  financialModelPreviewsEnabled = false,
  contentCmsHref,
  currentUser,
  hasPasskey = false,
  notesFeedEnabled = false,
  publicPreview = false,
  showDeveloperTools = false,
  showDeveloperAdminItems = false,
  pages,
  accessiblePageKeys,
  children,
}: PortalDashboardShellProps) {
  const pathname = useRenderedPathname();
  const shellPathname =
    publicPreview && pathname === "/" ? PORTAL_HOME_HREF : pathname;
  const isAdminRoute = !publicPreview && pathname.startsWith("/admin");
  const isDevRoute = !publicPreview && pathname.startsWith("/dev");
  const [accountOpen, setAccountOpen] = useState(false);
  const [accountActivated, setAccountActivated] = useState(false);
  const [showHiddenPages, setShowHiddenPages] = useShowHiddenPages();

  const accessibleKeySet = new Set(accessiblePageKeys);
  const releasedPages = pages.filter(
    (page) => capacityRiskTrackerEnabled || page.key !== "capacityRiskTracker",
  );
  const visiblePages = selectVisiblePages(
    releasedPages,
    accessibleKeySet,
    currentUser?.role ?? "subscriber",
    showHiddenPages,
    financialModelPreviewsEnabled && !publicPreview && Boolean(currentUser),
  );
  const activePage = findActivePortalPage(shellPathname, visiblePages);
  // Admins can open pages that are off or hidden for subscribers; the header
  // still names them even though the nav does not list them.
  const { label: shellTitle, icon: HeaderIcon } = resolveShellHeader(
    pathname,
    activePage ?? findActivePortalPage(shellPathname, releasedPages),
    isAdminRoute,
    isDevRoute,
  );
  const agentWorkbenchActive = isAgentWorkbenchRoute(pathname);
  // Agent workbenches bring their own history rail; start the portal nav as
  // an icon rail there so the transcript keeps its reading width. Each surface
  // remembers its own choice for the session.
  const [portalSidebarOpen, setPortalSidebarOpen] = useState(true);
  const [agentSidebarOpen, setAgentSidebarOpen] = useState(false);
  const fullBleed = isAdminRoute
    ? ADMIN_FULL_BLEED_PATHS.has(pathname)
    : PORTAL_FULL_BLEED_PATHS.has(pathname) ||
      pathname.startsWith("/models/") ||
      agentWorkbenchActive;

  const homeItem = visiblePages.find((page) => page.key === "home");
  const agentItem = visiblePages.find((page) => page.key === "agent");
  const canUseAgentPro =
    !publicPreview &&
    (advancedAgentEnabled || currentUser?.role === "admin");
  const categoryGroups = NAV_CATEGORY_ORDER.map((category) => ({
    category,
    items: visiblePages.filter(
      (page) =>
        page.category === category &&
        page.key !== "agent" &&
        page.key !== "home",
    ),
  })).filter((group) => group.items.length > 0);
  const showAdminLauncher = currentUser?.role === "admin";
  const homeHref = publicPreview ? "/" : PORTAL_HOME_HREF;
  const loginHref =
    pathname && pathname !== "/"
      ? `/login?next=${encodeURIComponent(pathname)}`
      : "/login";

  const portalSidebarPanel = (
    <div className="p-2 transition-[padding] duration-200 [transition-timing-function:cubic-bezier(0.77,0,0.175,1)] motion-reduce:transition-none group-data-[collapsible=icon]:px-0">
      <SidebarGroup>
        <SidebarGroupContent>
          <SidebarMenu>
            {homeItem ? (
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  isActive={isPageActive(shellPathname, homeItem)}
                  tooltip={homeItem.label}
                >
                  <Link href={homeHref}>
                    <Home />
                    <span>{homeItem.label}</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            ) : null}
            {agentItem || canUseAgentPro ? (
              <AgentSidebarNavItem
                agentItem={agentItem}
                advancedAgentEnabled={canUseAgentPro}
                pathname={pathname}
              />
            ) : null}
            {notesFeedEnabled ? (
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  isActive={pathname.startsWith("/notes")}
                  tooltip="Notes"
                >
                  <Link href="/notes">
                    <Newspaper />
                    <span>Notes</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            ) : null}
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>

      {categoryGroups.length > 0 ? (
        <SidebarSeparator className="my-1.5" />
      ) : null}

      {showDeveloperTools ? (
        <>
          <SidebarSeparator className="my-1.5" />
          <SidebarGroup>
            <SidebarGroupLabel>Developer</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {devNavItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <SidebarMenuItem key={item.href}>
                      <SidebarMenuButton
                        asChild
                        isActive={
                          pathname === item.href ||
                          Boolean(pathname?.startsWith(`${item.href}/`))
                        }
                        tooltip={item.label}
                      >
                        <Link href={item.href}>
                          <Icon />
                          <span>{item.label}</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </>
      ) : null}

      {categoryGroups.map((group, index) => (
        <Fragment key={group.category}>
          {index > 0 ? <SidebarSeparator className="my-1.5" /> : null}
          <SidebarGroup>
            <SidebarGroupLabel>{group.category}</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {group.items.map((page) => {
                  const Icon = iconMap[page.icon];
                  const isActive = isPageActive(pathname, page);
                  const isComingSoon = page.status === "coming-soon";
                  const isOffForSubscribers = !page.globallyEnabled;
                  const labelContent = (
                    <>
                      <Icon />
                      <span className="whitespace-nowrap">{page.label}</span>
                      {isComingSoon ? (
                        <Badge
                          variant="outline"
                          className="ml-auto border-border bg-muted text-muted-foreground text-[10px] group-data-[collapsible=icon]:hidden"
                        >
                          Soon
                        </Badge>
                      ) : isOffForSubscribers ? (
                        <Badge
                          variant="outline"
                          className="ml-auto border-border bg-muted text-muted-foreground text-[10px] group-data-[collapsible=icon]:hidden"
                        >
                          Off
                        </Badge>
                      ) : null}
                    </>
                  );

                  if (isComingSoon) {
                    return (
                      <SidebarMenuItem key={page.key}>
                        <SidebarMenuButton
                          aria-disabled
                          className="opacity-55"
                          tooltip={page.label}
                        >
                          {labelContent}
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    );
                  }

                  return (
                    <SidebarMenuItem key={page.key}>
                      <SidebarMenuButton
                        asChild
                        isActive={isActive}
                        tooltip={page.label}
                      >
                        <Link href={page.href}>{labelContent}</Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </Fragment>
      ))}
    </div>
  );

  return (
    <SidebarProvider
      className="h-dvh min-h-0 min-w-0 overflow-hidden"
      onOpenChange={agentWorkbenchActive ? setAgentSidebarOpen : setPortalSidebarOpen}
      open={agentWorkbenchActive ? agentSidebarOpen : portalSidebarOpen}
    >
      <Sidebar className="atlas-v2:[&_[data-sidebar=menu-button]]:min-h-9 atlas-v2:[&_[data-sidebar=menu-button]]:rounded-md" collapsible="icon">
        {/* Additive safe-area padding: the .safe-* component classes lose to
            the base p-2 padding utility (utilities layer wins the cascade). */}
        <SidebarHeader className="border-b border-sidebar-border pt-[calc(0.5rem+env(safe-area-inset-top))]">
          <div className="flex items-center gap-2 px-1.5 group-data-[collapsible=icon]:gap-0 group-data-[collapsible=icon]:px-0">
            <Link
              aria-label={`${brandNames.portalShort} home`}
              className="flex w-[108px] shrink-0 items-center overflow-hidden transition-[width,opacity,transform] duration-200 [transition-timing-function:cubic-bezier(0.77,0,0.175,1)] motion-reduce:transition-none hover:opacity-80 group-data-[collapsible=icon]:w-0 group-data-[collapsible=icon]:-translate-x-1 group-data-[collapsible=icon]:opacity-0 group-data-[collapsible=icon]:pointer-events-none"
              href={homeHref}
            >
              <BrandLogo
                className="h-12 w-[108px] max-w-full"
                priority
                variant="wordmark"
              />
            </Link>
            <SidebarTrigger className="ml-auto group-data-[collapsible=icon]:mx-auto" />
          </div>
        </SidebarHeader>
        <SidebarContent className="overflow-hidden p-0">
          <PortalMorphingSidebarNav
            adminActive={isAdminRoute}
            adminPanel={
              !publicPreview && currentUser?.role === "admin" ? (
                <PortalAdminSidebarNav
                  active={isAdminRoute}
                  contentCmsHref={contentCmsHref}
                  pathname={pathname}
                  showDeveloperItems={showDeveloperAdminItems}
                />
              ) : null
            }
            portalPanel={portalSidebarPanel}
          />
        </SidebarContent>
        <SidebarFooter className="pb-[calc(0.5rem+env(safe-area-inset-bottom))]">
          {publicPreview ? (
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton asChild tooltip="Support">
                  <Link href="/support">
                    <LifeBuoy />
                    <span>Support</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          ) : null}
          {publicPreview ? (
            <div className="grid gap-2 px-2 pb-1 group-data-[collapsible=icon]:hidden">
              <Button asChild size="sm">
                <Link href={loginHref}>Log in</Link>
              </Button>
              <Button asChild size="sm" variant="outline">
                <a href={SUBSCRIBE_URL}>Subscribe</a>
              </Button>
            </div>
          ) : currentUser ? (
            <AccountMenu
              className="w-full max-w-none justify-between group-data-[collapsible=icon]:size-8 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:border-transparent group-data-[collapsible=icon]:px-0 group-data-[collapsible=icon]:[&>svg:first-child]:mr-0 group-data-[collapsible=icon]:[&>span]:hidden group-data-[collapsible=icon]:[&>svg:last-child]:hidden"
              contentCmsHref={contentCmsHref}
              currentUser={currentUser}
              onOpenSettings={() => {
                setAccountActivated(true);
                setAccountOpen(true);
              }}
              onSettingsIntent={preloadAccountDialog}
              showBilling={billingManagementEnabled}
              surface={isAdminRoute ? "admin" : "portal"}
            />
          ) : null}
          {showAdminLauncher && !isAdminRoute && !publicPreview ? (
            <SidebarMenu>
              <AdminToolsSidebarNavItem contentCmsHref={contentCmsHref} />
            </SidebarMenu>
          ) : null}
        </SidebarFooter>
        <SidebarRail />
      </Sidebar>

      <SidebarInset className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        <header
          className={cn(
            "safe-top safe-x shrink-0 border-b border-border/60 bg-background/80 backdrop-blur-md atlas-v2:border-border atlas-v2:bg-background atlas-v2:backdrop-blur-none",
            agentWorkbenchActive && "hidden md:block",
          )}
        >
          <div className="flex h-14 items-center gap-2 px-3 sm:px-4 atlas-v2:h-16 atlas-v2:gap-3 atlas-v2:px-4 atlas-v2:sm:px-6">
            <SidebarTrigger className="touch-target -ml-1 md:hidden" />
            <div className="flex min-w-0 items-center gap-2">
              {HeaderIcon ? (
                <HeaderIcon className="h-4 w-4 shrink-0 text-primary" />
              ) : null}
              <h1 className="truncate text-sm font-semibold text-foreground">
                {shellTitle}
              </h1>
            </div>
            {publicPreview ? (
              <div className="ml-auto flex shrink-0 items-center gap-1.5">
                <Button asChild size="sm" variant="ghost">
                  <Link href={loginHref}>Log in</Link>
                </Button>
                <Button asChild className="hidden sm:inline-flex" size="sm">
                  <a href={SUBSCRIBE_URL}>Subscribe</a>
                </Button>
              </div>
            ) : null}
          </div>
        </header>
        {!publicPreview ? (
          <div className="shrink-0">
            <PasskeyEnrollmentBanner hasPasskey={hasPasskey} />
          </div>
        ) : null}
        {fullBleed ? (
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            {children}
          </div>
        ) : isAdminRoute || isDevRoute ? (
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain">
            <div className="safe-x">
              <div className="mx-auto w-full max-w-6xl px-4 py-6 sm:px-6 lg:px-8">
                {children}
              </div>
            </div>
          </div>
        ) : (
          <div className="min-h-0 min-w-0 flex-1 overflow-x-clip overflow-y-auto overscroll-y-contain">
            <div className="safe-x">{children}</div>
          </div>
        )}
        {currentUser && accountActivated ? (
          <LazyAccountDialog
            billingManagementEnabled={billingManagementEnabled}
            currentUser={currentUser}
            onOpenChange={setAccountOpen}
            onShowHiddenPagesChange={setShowHiddenPages}
            open={accountOpen}
            showHiddenPages={showHiddenPages}
          />
        ) : null}
      </SidebarInset>
    </SidebarProvider>
  );
}

function resolveShellHeader(
  pathname: string,
  activePage: PortalPageState | undefined,
  isAdminRoute: boolean,
  isDevRoute: boolean,
): { label: string; description?: string; icon?: LucideIcon } {
  if (isAdminRoute) {
    return findActiveAdminNavItem(pathname) ?? { label: "Admin" };
  }
  if (isDevRoute) {
    return findActiveDevNavItem(pathname) ?? { label: "Developer", icon: Wrench };
  }
  if (activePage) {
    return { ...activePage, icon: iconMap[activePage.icon] };
  }
  if (pathname.startsWith("/notes")) {
    return {
      label: "Notes",
      description: "Research notes and market commentary from the Creative Strategies research team.",
    };
  }
  if (pathname === "/dashboard/billing") {
    return {
      label: "Billing",
      description: "Plan, renewal, and payment details for your CS Atlas subscription.",
      icon: CreditCard,
    };
  }
  if (pathname === "/dashboard/support") {
    return {
      label: "Support",
      description: "Account, subscription, and research support from the Creative Strategies team.",
      icon: LifeBuoy,
    };
  }
  if (pathname.startsWith(ADVANCED_AGENT_HREF)) {
    return {
      label: brandNames.agentProShort,
      description: "Deeper research and analysis with web search and files.",
      icon: ShieldCheck,
    };
  }
  return { label: brandNames.portalShort };
}

function AdminToolsSidebarNavItem({ contentCmsHref }: { contentCmsHref?: string }) {
  const { isMobile, state } = useSidebar();
  const choices = [
    { href: "/admin/users", label: "Admin", icon: ShieldCheck, external: false },
    ...(contentCmsHref
      ? [{ href: contentCmsHref, label: "Content CMS", icon: SquarePen, external: true }]
      : []),
  ];

  if (state === "collapsed" && !isMobile) {
    return (
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton tooltip="Admin tools" aria-label="Admin tools">
              <ShieldCheck />
              <span>Admin tools</span>
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" side="right">
            <DropdownMenuGroup>
              <DropdownMenuLabel>Admin tools</DropdownMenuLabel>
              {choices.map((choice) => (
                <DropdownMenuItem asChild className="min-h-11" key={choice.href}>
                  {choice.external ? (
                    <a href={choice.href} rel="noopener noreferrer" target="_blank">
                      <choice.icon />
                      {choice.label}
                    </a>
                  ) : (
                    <Link href={choice.href}>
                      <choice.icon />
                      {choice.label}
                    </Link>
                  )}
                </DropdownMenuItem>
              ))}
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    );
  }

  return (
    <SidebarMenuItem>
      <Collapsible className="group/admin-tools" defaultOpen={false}>
        <CollapsibleTrigger asChild>
          <SidebarMenuButton className="h-11" tooltip="Admin tools">
            <ShieldCheck />
            <span>Admin tools</span>
            <ChevronRight className="ml-auto transition-transform duration-200 motion-reduce:transition-none group-data-[state=open]/admin-tools:rotate-90" />
          </SidebarMenuButton>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <SidebarMenuSub>
            {choices.map((choice) => (
              <SidebarMenuSubItem key={choice.href}>
                <SidebarMenuSubButton asChild className="h-11">
                  {choice.external ? (
                    <a href={choice.href} rel="noopener noreferrer" target="_blank">
                      <choice.icon />
                      <span>{choice.label}</span>
                    </a>
                  ) : (
                    <Link href={choice.href}>
                      <choice.icon />
                      <span>{choice.label}</span>
                    </Link>
                  )}
                </SidebarMenuSubButton>
              </SidebarMenuSubItem>
            ))}
          </SidebarMenuSub>
        </CollapsibleContent>
      </Collapsible>
    </SidebarMenuItem>
  );
}

function isAgentWorkbenchRoute(pathname: string | null) {
  if (!pathname) return false;
  return (
    pathname === BASIC_AGENT_HREF ||
    pathname.startsWith(`${BASIC_AGENT_HREF}/`) ||
    pathname === ADVANCED_AGENT_HREF ||
    pathname.startsWith(`${ADVANCED_AGENT_HREF}/`)
  );
}

function isAgentHrefActive(pathname: string | null, href: string) {
  if (!pathname) return false;
  return href === BASIC_AGENT_HREF
    ? pathname === BASIC_AGENT_HREF
    : pathname === href || pathname.startsWith(`${href}/`);
}

type AgentSidebarNavItemProps = {
  agentItem?: PortalPageState;
  advancedAgentEnabled: boolean;
  pathname: string | null;
};

function AgentSidebarNavItem({
  agentItem,
  advancedAgentEnabled,
  pathname,
}: AgentSidebarNavItemProps) {
  const { isMobile, state } = useSidebar();
  const choices = [
    ...(agentItem
      ? [{
          href: agentItem.href,
          label: brandNames.agentShort,
          icon: Bot,
        }]
      : []),
    ...(advancedAgentEnabled
      ? [{
          href: ADVANCED_AGENT_HREF,
          label: brandNames.agentProShort,
          icon: ShieldCheck,
        }]
      : []),
  ];
  const agentRouteActive =
    isAgentHrefActive(pathname, BASIC_AGENT_HREF) ||
    isAgentHrefActive(pathname, ADVANCED_AGENT_HREF);
  const [menuOpen, setMenuOpen] = useState(agentRouteActive);
  // Open the submenu when navigation enters an agent route, without a synchronous
  // setState-in-effect: adjust state during render off the previous value (the
  // React-recommended pattern for "reacting to a prop change").
  const [wasAgentRouteActive, setWasAgentRouteActive] =
    useState(agentRouteActive);
  if (agentRouteActive !== wasAgentRouteActive) {
    setWasAgentRouteActive(agentRouteActive);
    if (agentRouteActive) {
      setMenuOpen(true);
    }
  }
  const iconCollapsed = state === "collapsed" && !isMobile;

  if (choices.length === 0) return null;

  if (choices.length === 1) {
    const choice = choices[0]!;
    const Icon = choice.icon;
    const active = isAgentHrefActive(pathname, choice.href);
    return (
      <SidebarMenuItem>
        <SidebarMenuButton
          asChild
          isActive={active}
          tooltip={choice.label}
        >
          <Link aria-current={active ? "page" : undefined} href={choice.href}>
            <Icon />
            <span>{choice.label}</span>
          </Link>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  }

  if (iconCollapsed) {
    return (
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton isActive={agentRouteActive} tooltip="Agents">
              <Bot />
              <span>Agents</span>
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-48" side="right">
            <DropdownMenuGroup>
              <DropdownMenuLabel>Choose an agent</DropdownMenuLabel>
              {choices.map((choice) => {
                const active = isAgentHrefActive(pathname, choice.href);
                const Icon = choice.icon;
                return (
                  <DropdownMenuItem asChild key={choice.href} className="min-h-11">
                    <Link
                      aria-current={active ? "page" : undefined}
                      aria-label={choice.label}
                      href={choice.href}
                    >
                      <Icon aria-hidden />
                      <span className="min-w-0 flex-1 font-medium">{choice.label}</span>
                      {active ? <Check aria-hidden /> : null}
                    </Link>
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    );
  }

  return (
    <SidebarMenuItem>
      <Collapsible
        className="group/collapsible"
        onOpenChange={setMenuOpen}
        open={menuOpen}
      >
        <CollapsibleTrigger asChild>
          <SidebarMenuButton isActive={agentRouteActive} tooltip="Agents">
            <Bot />
            <span>Agents</span>
            <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
          </SidebarMenuButton>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <SidebarMenuSub>
            {choices.map((choice) => {
              const active = isAgentHrefActive(pathname, choice.href);
              return (
                <SidebarMenuSubItem key={choice.href}>
                  <SidebarMenuSubButton asChild className="h-11" isActive={active}>
                    <Link
                      aria-current={active ? "page" : undefined}
                      aria-label={choice.label}
                      href={choice.href}
                    >
                      <span className="min-w-0 flex-1 font-medium">{choice.label}</span>
                      {active ? <Check aria-hidden /> : null}
                    </Link>
                  </SidebarMenuSubButton>
                </SidebarMenuSubItem>
              );
            })}
          </SidebarMenuSub>
        </CollapsibleContent>
      </Collapsible>
    </SidebarMenuItem>
  );
}

export function findActivePortalPage(
  pathname: string | null,
  pages: readonly PortalPageState[],
): PortalPageState | undefined {
  if (!pathname) return undefined;
  const exact = pages.find((page) => page.href === pathname);
  if (exact) return exact;
  return pages
    .filter(
      (page) =>
        page.href !== PORTAL_HOME_HREF && pathname.startsWith(page.href),
    )
    .sort((a, b) => b.href.length - a.href.length)[0];
}

function isPageActive(pathname: string | null, page: PortalPageState) {
  if (!pathname) return false;
  if (page.href === PORTAL_HOME_HREF) {
    return pathname === PORTAL_HOME_HREF;
  }
  return pathname === page.href || pathname.startsWith(`${page.href}/`);
}

function selectVisiblePages(
  pages: readonly PortalPageState[],
  accessibleKeySet: Set<PortalPageKey>,
  role: PortalRole,
  showHiddenPages: boolean,
  financialModelPreviewsEnabled: boolean,
): PortalPageState[] {
  return pages.filter((page) => {
    const canPreview = page.key === "financialModels" && financialModelPreviewsEnabled;
    if (!accessibleKeySet.has(page.key) && !canPreview) {
      return false;
    }
    if (page.status === "admin-mounted" && role !== "admin") {
      return false;
    }
    return isPortalPageListedForMember({ page, role, showHiddenPages });
  });
}
